import React, { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { Controller, useWatch } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import FormWarningLabel from 'components/atoms/FormWarningLabel';
import { StyledFormElementWrapper } from 'theme/styles';
import { IFormElementOptionProp, IFormElementProps } from 'models/form';
import { EDictionaryScope } from 'models/dictionaryScopeContext';
import { useArchivedOptions } from 'hooks/useArchivedOptions';
import { useArchivedValueConfirm } from 'hooks/useArchivedValueConfirm';
import { colors } from 'theme/colors';
import { IconButton } from '@mui/material';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { useTranslations } from 'hooks/useTranslations';
import { StyledFormControl, StyledInnerWrapper, StyledSelect } from './styles';
import { getNestedErrorMessage } from './helpers';
import { ESelectSizesPX } from 'constants/Select';

interface IFormSelect extends IFormElementProps {
  options: IFormElementOptionProp[];
  disabled?: boolean;
  required?: boolean;
  withValidation?: boolean;
  withLabel?: boolean;
  setValue: any;
  setValueAdditionalBehavior?: (value: any) => void;
  className?: string;
  canBeEmpty?: boolean;
  arrayName?: string;
  arrayIndex?: number;
  fieldName?: string;
  onSelect?: (arg: any) => void;
  translate?: boolean;
  sizePX?: ESelectSizesPX;
  placeholder?: string;
  dictionaryScope?: EDictionaryScope;
}

export const FormSelect = ({
  name,
  label,
  options,
  control,
  errors,
  disabled,
  required,
  className,
  setValue,
  setValueAdditionalBehavior,
  arrayName,
  arrayIndex,
  canBeEmpty = true,
  fieldName,
  withValidation = true,
  withLabel = true,
  onSelect,
  translate = false,
  sizePX,
  placeholder,
  dictionaryScope,
}: IFormSelect) => {
  const { t } = useTranslations();
  const [error, setError] = useState<string>('');
  const watchedValue = useWatch({ control, name });
  const { visibleOptions, hasArchivedSelection, isArchivedValue } = useArchivedOptions(
    options,
    watchedValue,
    dictionaryScope,
  );
  const { confirmChange, archivedConfirmModal } = useArchivedValueConfirm();

  useEffect(() => {
    let isError = false;
    if (name.includes('.') && !name.includes('[') && Object.keys(errors).length) {
      const keys = name.split('.');
      const errorMessage = getNestedErrorMessage(errors, keys);
      if (keys && errorMessage) {
        isError = true;
        setError(errorMessage);
      }
    } else {
      const errorMessage =
        arrayName && arrayIndex !== undefined && fieldName
          ? errors?.[arrayName]?.[arrayIndex]?.[fieldName]?.message
          : errors[name]?.message;

      isError = true;
      setError(errorMessage);
    }
    if (!isError) {
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, name]);

  return (
    <StyledFormElementWrapper className={className}>
      {withLabel && (
        <FormLabel name={name} label={label} disabled={disabled} required={required} />
      )}
      <StyledInnerWrapper sizePx={sizePX ?? ESelectSizesPX.px32}>
        <Controller
          name={name}
          control={control}
          defaultValue=""
          render={({ field }) => (
            <StyledFormControl className={className} error={!!errors[name]?.message}>
              <StyledSelect
                {...field}
                displayEmpty
                labelId={`${name}-label`}
                endAdornment={
                  field.value !== '' &&
                  canBeEmpty &&
                  !disabled && (
                    <IconButton
                      sx={{
                        maxHeight: sizePX ? `{${sizePX}px` : '32px',
                        maxWidth: sizePX ? `{${sizePX}px` : '32px',
                      }}
                      onClick={() => {
                        confirmChange(hasArchivedSelection, () => {
                          setValue(name, '');
                          if (setValueAdditionalBehavior) {
                            setValueAdditionalBehavior('');
                          }
                        });
                      }}
                      id="clear"
                    >
                      <Icon
                        type={EIconTypes.clear}
                        color={disabled ? colors.gray400 : colors.gray900}
                      />
                    </IconButton>
                  )
                }
                id={name}
                disabled={disabled}
                error={!!error}
                onChange={(e) => {
                  const newValue = e.target.value;
                  confirmChange(hasArchivedSelection, () => {
                    field.onChange(newValue);
                    if (onSelect) {
                      onSelect(newValue);
                      if (setValueAdditionalBehavior) {
                        setValueAdditionalBehavior(newValue);
                      }
                    }
                  });
                }}
                sx={{
                  ...(field.value === '' ? { color: colors.gray400 } : {}),
                  '& .MuiSelect-icon': {
                    display: canBeEmpty && !disabled && field.value !== '' ? 'none' : '',
                  },
                }}
                sizePx={sizePX ?? ESelectSizesPX.px32}
              >
                <MenuItem
                  disabled={!canBeEmpty}
                  value=""
                  style={{
                    display: 'none',
                  }}
                >
                  {canBeEmpty && !placeholder ? t('formSelect.all.placeholder') : ''}
                  {placeholder ? t(placeholder) : ''}
                </MenuItem>
                {visibleOptions.map((option: IFormElementOptionProp) => {
                  const isArchived = isArchivedValue(option.value);
                  return (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={isArchived}
                      sx={isArchived ? { '&.Mui-disabled': { opacity: 1 } } : undefined}
                    >
                      {translate ? t(option.label) : option.label}
                      {isArchived ? ` ${t('dictionary.archivedValue.option.suffix')}` : ''}
                    </MenuItem>
                  );
                })}
              </StyledSelect>
            </StyledFormControl>
          )}
        />
      </StyledInnerWrapper>
      {hasArchivedSelection && (
        <FormWarningLabel
          name={name}
          label={t('dictionary.archivedValue.warning')}
          style={{ marginTop: '3px' }}
        />
      )}
      {withValidation && <FormErrorLabel label={error} style={{ marginTop: '3px' }} />}
      {archivedConfirmModal}
    </StyledFormElementWrapper>
  );
};
